/**
 * Route path contract.
 *
 * Single source of truth for all client-side route paths. Lane B uses these in
 * `useNavigate` calls and in <Route path={...}> definitions. Lane C wires the
 * router with the same set.
 *
 * If a route is added or removed, update this file. Do not hardcode route strings.
 */

export const ROUTES = Object.freeze({
  SPLASH: "/",
  ORACLE: "/oracle",
  // Signed-in home. The post-login landing surface: last score, movement,
  // category readout, tonight's routine. Deliberately NOT /create — the
  // scanner used to double as the home screen, which meant a returning member
  // was greeted by a capture viewport and a first-run tutorial instead of
  // their own progress.
  HOME: "/home",
  // Public content pages (no auth). /legal redirects to /legal/terms.
  LEGAL: "/legal",
  LEGAL_TERMS: "/legal/terms",
  LEGAL_PRIVACY: "/legal/privacy",
  ABOUT: "/about",
  FAQ: "/faq",
  CONTACT: "/contact",
  SIGN_IN: "/sign-in",
  // Mandatory onboarding, collapsed to a single "essentials" screen (age +
  // terms); gender and handle are deferred, see contracts/onboarding.js.
  // AuthGate forces any authenticated user without onboarding_complete here.
  ONBOARDING: "/onboarding",
  // Unified Create surface (design-recreation): scan capture + glowup
  // builder behind a segmented switch. /oracle and /glowups/new redirect here.
  CREATE: "/create",
  // Account surface (design-recreation): profile, preferences, sign out,
  // delete account.
  ACCOUNT: "/account",
  // Routine surface: the persistent product plan (strength ladders, AM/PM,
  // adherence check-in). Products live here, out of the verdict/Vault.
  ROUTINE: "/routine",
  // Glowup (Phase 1, Lane C)
  GLOWUPS: "/glowups",
  GLOWUPS_NEW: "/glowups/new",
  GLOWUP_DETAIL: "/glowups/:glowup_id",
  // Explore. The surface is named EXPLORE in the UI and the path now says
  // /feedback rather than /judge, matching the vocabulary everywhere else.
  //
  // JUDGE_LEGACY is the old path, kept as a REDIRECT ONLY (see App.js). A URL
  // is a promise: /judge is in bookmarks, in browser history, and — the part
  // that cannot be fixed later — inside `?unlock=` deep links that have already
  // gone out. Nothing should ever link to it; App.js is the only file that may
  // reference it.
  JUDGE: "/feedback",
  JUDGE_LEGACY: "/judge",
  // Vault (Phase 1, Lane A)
  VAULT: "/vault",
  VAULT_ENTRY: "/vault/:scan_id",
  // Membership (Stripe). Deliberately NOT in the nav — BottomTabBar caps at 4
  // tabs and is full, so Account's MANAGE MEMBERSHIP button is the primary way
  // in; ScanJudge's empty-deck panel links here too. The paywalls do NOT: both
  // MembershipCTA and the Routine paywall call useStartTrial, which goes
  // straight to Stripe Checkout. SUCCESS reads the live entitlement doc; it never trusts a query
  // param, because the webhook may not have landed when Stripe redirects back.
  MEMBERSHIP: "/membership",
  MEMBERSHIP_SUCCESS: "/membership/success",
  MEMBERSHIP_CANCEL: "/membership/cancel",
});

// Routes that require an authenticated Firebase Auth user.
// The AuthGate wrapper redirects unauthenticated visitors to SIGN_IN
// and preserves the original destination so post-auth they land where
// they meant to go.
export const AUTH_GATED_ROUTES = Object.freeze([
  ROUTES.ORACLE,
  ROUTES.HOME,
  ROUTES.ONBOARDING,
  ROUTES.CREATE,
  ROUTES.ACCOUNT,
  ROUTES.ROUTINE,
  ROUTES.GLOWUPS,
  ROUTES.GLOWUPS_NEW,
  ROUTES.JUDGE,
  ROUTES.VAULT,
  ROUTES.VAULT_ENTRY,
  ROUTES.MEMBERSHIP,
  ROUTES.MEMBERSHIP_SUCCESS,
  ROUTES.MEMBERSHIP_CANCEL,
]);

export function glowupDetailPath(glowupId) {
  return `/glowups/${glowupId}`;
}

export function vaultEntryPath(scanId) {
  return `/vault/${scanId}`;
}
